import {
	Anchor,
	FontIconName,
	Icon,
	MediaPlaceholder,
	IconButton,
	Typography,
	ConditionalWrapper,
	FormattedText
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import { never } from "client_legacy/ndaw/errors";
import styles from "./HubDetailsLayout.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	MediaQueryKind,
	useCallbackWithoutArguments,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemMediaKind } from "client/Api/Hub/common";
import classNames from "classnames";
import { ActiveFilter, ImageUrlWithAttribution } from "./common";
import { UserHtml } from "../UserHtml/UserHtml";
import { VideoViewer } from "./Builder/Viewer/Elements/Video/Video";
import { HubBackButton } from "./HubBackButton";
import { HubDetailsContentFooter } from "./HubDetailsContentFooter";
import { ImageAttributionViewModel } from "client/Api/common";

export const HubDetailsLayout = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<article>
			{children}
		</article>
	);
};
HubDetailsLayout.displayName = "HubDetailsLayout";

export type HubDetailsLayoutTitleKind =
	| "primary"
	| "secondary";

export type HubDetailsLayoutTitleProps = {
	text: string;
	isPinned?: boolean;
	kind?: HubDetailsLayoutTitleKind;
	onEdit?: () => void;
};

export const HubDetailsLayoutTitle = (props: HubDetailsLayoutTitleProps): ReactElement => {
	const {
		text,
		isPinned,
		kind = "primary",
		onEdit: onEditFromProps
	} = props;

	const onEdit: (() => void) | undefined = useCallbackWithoutArguments(onEditFromProps);

	return (
		<div className={styles.titleAndEdit}>
			<h2
				{...setTrackingId("title")}
				className={styles.title}
			>
				{isPinned && (
					<span className={styles.pinIcon}>
						<Icon
							size="extra-large"
							value={
								{
									kind:"font",
									name:"thumbtack",
									color:"primary"
								}
							}
						/>
					</span>
				)}
				<ConditionalWrapper
					condition={kind === "secondary"}
					wrapper={(children) => <Typography textColor="secondary">{children}</Typography>}
				>
					{text}
				</ConditionalWrapper>
			</h2>

			{onEdit && (
				<div className={styles.editButton}>
					<IconButton
						kind="secondary"
						icon="pen"
						trackingId="edit-button"
						onClick={onEdit}
						shape="round"
					/>
				</div>
			)}
		</div>
	);
};
HubDetailsLayoutTitle.displayName = "HubDetailsLayout.Title";

export type HubDetailsLayoutBackButtonProps = {
	activeFilter: ActiveFilter | nil;
	selectedCategoryName?: string;
	onBack: () => void;
};

export const HubDetailsLayoutBackButton = (props: HubDetailsLayoutBackButtonProps): ReactElement => {
	const {
		activeFilter,
		selectedCategoryName,
		onBack: onBackFromProps,
	} = props;

	const onBack: (() => void) | undefined = useCallbackWithoutArguments(onBackFromProps);

	return (
		<div className={styles.backContainer}>
			<HubBackButton
				activeFilter={activeFilter}
				selectedCategoryName={selectedCategoryName}
				onBack={onBack}
				shouldHideOnResponsive
			/>
		</div>
	);
};
HubDetailsLayoutBackButton.displayName = "HubDetailsLayout.BackButton";

type HubDetailsLayoutSimpleTitleProps = {
	text: string;
	isPinned?: boolean;
	kind?: HubDetailsLayoutTitleKind;
};

const HubDetailsLayoutSimpleTitle = (props: HubDetailsLayoutSimpleTitleProps): ReactElement => {
	const {
		text,
		isPinned,
		kind = "primary"
	} = props;

	return (
		<div>
			<div className={styles.titleAndEdit}>
				<h2
					{...setTrackingId("title")}
					className={styles.title}
				>
					{isPinned && (
						<span className={styles.pinIcon}>
							<Icon
								size="extra-large"
								value={
									{
										kind: "font",
										name: "thumbtack",
										color: "primary"
									}
								}
							/>
						</span>
					)}
					<ConditionalWrapper
						condition={kind === "secondary"}
						wrapper={(children) => <Typography textColor="secondary">{children}</Typography>}
					>
						{text}
					</ConditionalWrapper>
				</h2>
			</div>
		</div>
	);
};
HubDetailsLayoutSimpleTitle.displayName = "HubDetailsLayout.SimpleTitle";

export const HubDetailsLayoutSubtitle = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			{...setTrackingId("info")}
			className={styles.subtitle}
		>
			{children}
		</div>
	);
};
HubDetailsLayoutSubtitle.displayName = "HubDetailsLayout.Subtitle";

type HubDetailsLayoutSubtitleTextProps = {
	tooltip?: string;
	shouldNotShrink?: boolean;
};

const HubDetailsLayoutSubtitleText = (props: PropsWithChildren<HubDetailsLayoutSubtitleTextProps>): ReactElement => {
	const {
		tooltip,
		shouldNotShrink = false,
		children
	} = props;

	return (
		<span
			className={classNames(
				styles.subtitleText,
				{[styles.subtitleText_shouldNotShrink]: shouldNotShrink}
			)}
			title={tooltip}
		>
			{children}
		</span>
	);
};
HubDetailsLayoutSubtitleText.displayName = "HubDetailsLayout.SubtitleText";

export type HubDetailsLayoutSubtitleLinkProps = {
	onClick?: () => void;
	trackingId?: string;
	tooltip?: string;
	shouldNotWrap?: boolean;
};

export const HubDetailsLayoutSubtitleLink = (props: PropsWithChildren<HubDetailsLayoutSubtitleLinkProps>): ReactElement => {
	const {
		onClick,
		trackingId,
		tooltip,
		shouldNotWrap = false,
		children
	} = props;

	if (!onClick) {
		return (
			<span
				className={
					classNames(
						styles.subtitleText,
						{[styles.subtitleText_shouldNotWrap]: shouldNotWrap}
					)
				}
				title={tooltip}
			>
				{children}
			</span>
		);
	}

	return (
		<Anchor
			trackingId={trackingId}
			className={
				classNames(
					styles.subtitleLink,
					{[styles.subtitleLink_shouldNotWrap]: shouldNotWrap}
				)
			}
			onClick={() => onClick()}
			title={tooltip}
		>
			{children}
		</Anchor>
	);
};
HubDetailsLayoutSubtitleLink.displayName = "HubDetailsLayout.SubtitleLink";

export type MediaContentModel = {
	mediaKind: HubItemMediaKind.None;
} | {
	mediaKind: HubItemMediaKind.Image;
	imageUrl: string;
	attribution: ImageAttributionViewModel | null;
} | {
	mediaKind: HubItemMediaKind.Video;
	videoUrl: string;
} | {
	mediaKind: HubItemMediaKind.Embed;
	embedCode: string;
};

export type HubDetailsLayoutMediaProps = {
	media: MediaContentModel;
	hubItemKind: HubItemKind;
	onViewImage?: (image: ImageUrlWithAttribution) => void;
};

export const HubDetailsLayoutMedia = (props: HubDetailsLayoutMediaProps): ReactElement => {
	const {
		media,
		hubItemKind,
		onViewImage
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const mediaPlaceHolderIcon = useMemo(
		(): FontIconName => {
			switch(hubItemKind) {
				case HubItemKind.Announcement:
					return "bullhorn";
				case HubItemKind.KnowledgeItem:
					return "book";
				case HubItemKind.Event:
					return "calendar-star";
				default:
					never(hubItemKind);
			}
		},
		[hubItemKind]
	);

	const renderContent = (): ReactElement | null => {
		switch (media.mediaKind) {
			case HubItemMediaKind.None:
				return (
					<div className={styles.media_placeholder}>
						<MediaPlaceholder
							icon={mediaPlaceHolderIcon}
							size={
								isMobile
									? "large"
									: "extra-large"
							}
						/>
					</div>
				);
			case HubItemMediaKind.Image:
				const clickHandler: (() => void) | undefined = onViewImage
					? (): void => onViewImage({
						url: media.imageUrl,
						attribution: media.attribution
					})
					: undefined;

				return (
					<div
						className={classNames(
							styles.imageHost,
							{ [styles.imageHost_clickable]: clickHandler }
						)}
						onClick={clickHandler}
					>
						<img src={media.imageUrl} />
					</div>
				);
			case HubItemMediaKind.Embed:
				return (
					<UserHtml
						html={media.embedCode}
						trackingId="embed-media-iframe"
					/>
				);
			case HubItemMediaKind.Video:
				return <VideoViewer url={media.videoUrl} />;
			default:
				never(media);
		}
	};

	return (
		<section {...setTrackingId("media")} className={styles.media}>
			{renderContent()}
		</section>
	);
};
HubDetailsLayoutMedia.displayName = "HubDetailsLayout.Media";

export const HubDetailsLayoutContent = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<section {...setTrackingId("content")} className={styles.content}>
			{children}
		</section>
	);
};
HubDetailsLayoutContent.displayName = "HubDetailsLayout.Content";

const HubDetailsLayoutSeparator = (): ReactElement => {
	return (
		<hr className={styles.lineSeparator} />
	);
};
HubDetailsLayoutSeparator.displayName = "HubDetailsLayoutSeparator";

type HubDetailsLayoutLinkedDocumentsProps = {
	count: number;
};

const HubDetailsLayoutLinkedDocuments = (props: PropsWithChildren<HubDetailsLayoutLinkedDocumentsProps>): ReactElement => {
	const {
		count,
		children
	} = props;

	return (
		<section className={styles.linkedDocuments}>
			<h4 className={styles.linkedDocuments_title}>
				{count === 1
					? localize("_1LinkedDocument")
					: (
						<FormattedText
							text={localize("_NLinkedDocuments")}
							thingsToReplace={[
								{ what: "{{N}}", filler: count.toString() }
							]}
						/>
					)}
			</h4>
			{children}
		</section>
	);
};
HubDetailsLayoutLinkedDocuments.displayName = "HubDetailsLayout.LinkedDocuments";

HubDetailsLayout.Title = HubDetailsLayoutTitle;
HubDetailsLayout.SimpleTitle = HubDetailsLayoutSimpleTitle;
HubDetailsLayout.Subtitle = HubDetailsLayoutSubtitle;
HubDetailsLayout.SubtitleText = HubDetailsLayoutSubtitleText;
HubDetailsLayout.SubtitleLink = HubDetailsLayoutSubtitleLink;
HubDetailsLayout.Media = HubDetailsLayoutMedia;
HubDetailsLayout.Content = HubDetailsLayoutContent;
HubDetailsLayout.Separator = HubDetailsLayoutSeparator;
HubDetailsLayout.Footer = HubDetailsContentFooter;
HubDetailsLayout.LinkedDocuments = HubDetailsLayoutLinkedDocuments;
HubDetailsLayout.BackButton = HubDetailsLayoutBackButton;